// ./src/utils/servicePoints.ts
//
// ROADMAP #169 — service points (entrances, desks, merch tables, bars): the kinds (mirroring the API's
// SERVICE_POINT_KIND, boundary/venue.ts), how each scanning page orders them, and the choice a device remembers.
// Pure (no React, no barrel imports) so the guards run it.

export const SERVICE_POINT_KIND = {
  ENTRANCE: "entrance",
  RECEPTION: "reception",
  MERCH: "merch",
  INFO: "info",
  BAR_FOOD: "bar_food",
  OTHER: "other",
} as const;

export type ServicePointKind = (typeof SERVICE_POINT_KIND)[keyof typeof SERVICE_POINT_KIND];

export const SERVICE_POINT_KIND_LABELS: Record<ServicePointKind, string> = {
  entrance: "Entrance",
  reception: "Reception desk",
  merch: "Merch / pick-up",
  info: "Info desk",
  bar_food: "Bar / food",
  other: "Other",
};

export const SERVICE_POINT_KIND_OPTIONS = (Object.keys(SERVICE_POINT_KIND_LABELS) as ServicePointKind[]).map((value) => ({
  value,
  label: SERVICE_POINT_KIND_LABELS[value],
}));

/**
 * ROADMAP #176 — what is done at a point (mirrors the API's SERVICE_POINT_ACTIVITY): check people in (and out), hand out
 * ticket add-ons, or both. A point's own is `activity`; null = its kind's default. The check-in console follows the point
 * it stands at; the server records it and refuses nothing for it (owner, 2026-10-17).
 */
export const SERVICE_POINT_ACTIVITY = {
  CHECK_IN: "check_in",
  HAND_OUT: "hand_out",
  BOTH: "both",
  // #68 — the lost & found desk: neither checks people in nor hands anything out (the door never offers it).
  LOST_FOUND: "lost_found",
} as const;

export type ServicePointActivity = (typeof SERVICE_POINT_ACTIVITY)[keyof typeof SERVICE_POINT_ACTIVITY];

export const SERVICE_POINT_ACTIVITY_LABELS: Record<ServicePointActivity, string> = {
  check_in: "Check-in",
  hand_out: "Hand-out",
  both: "Check-in & hand-out",
  lost_found: "Lost & found",
};

export const SERVICE_POINT_ACTIVITY_OPTIONS = (Object.keys(SERVICE_POINT_ACTIVITY_LABELS) as ServicePointActivity[]).map((value) => ({
  value,
  label: SERVICE_POINT_ACTIVITY_LABELS[value],
}));

/** A kind's activity (mirrors SERVICE_POINT_KIND_DEFAULT_ACTIVITY): an entrance checks in, the merch table hands out. */
export const SERVICE_POINT_KIND_DEFAULT_ACTIVITY: Record<ServicePointKind, ServicePointActivity> = {
  entrance: "check_in",
  reception: "both",
  merch: "hand_out",
  info: "both",
  // A voucher stall — the door treats it as both.
  bar_food: "both",
  other: "both",
};

const isActivity = (v: unknown): v is ServicePointActivity => typeof v === "string" && v in SERVICE_POINT_ACTIVITY_LABELS;

/** The kind's default activity (an unknown kind: both). */
export const defaultActivityOf = (kind: string | null | undefined): ServicePointActivity =>
  SERVICE_POINT_KIND_DEFAULT_ACTIVITY[(kind ?? "other") as ServicePointKind] ?? "both";

/** What is done at a point: its own activity, else its kind's. No point chosen → both (the console as it always was). */
export const pointActivity = (p: { kind?: string | null; activity?: string | null } | null | undefined): ServicePointActivity => {
  if (!p) {
    return "both";
  }

  return isActivity(p.activity) ? p.activity : defaultActivityOf(p.kind);
};

/**
 * What a point STORES for a chosen activity: null when it is the kind's own (so the point keeps following its kind —
 * change the kind and the activity moves with it), else the choice.
 */
export const storedActivity = (kind: string | null | undefined, activity: string | null | undefined): ServicePointActivity | null =>
  isActivity(activity) && activity !== defaultActivityOf(kind) ? activity : null;

/** Does the console show this row at a point doing `activity`? People at a check-in, add-ons at a hand-out, both at both. */
export const shownAtActivity = (activity: ServicePointActivity, isAddOn: boolean): boolean =>
  activity === "both" || (activity === "hand_out") === isAddOn;

/** The door console's mode (EventCheckinPage): check people in, out, or only look. */
export type DoorMode = "check_in" | "check_out" | "verify";

export interface DoorMember {
  isAddOn: boolean;
  checkedInAt: string | null;
  checkedOutAt: string | null;
  /** A volunteer: their scan also checks their SHIFT in and out — even when their own row was never stamped. */
  isVolunteer?: boolean;
}

/**
 * #228 — a scan IN on somebody who checked OUT: a RE-ENTRY (the server decides whether the event or the ticket type
 * allows it). Never an add-on: it is handed out, not checked in and out.
 */
export const isReentryScan = (m: DoorMember, mode: DoorMode): boolean => mode === "check_in" && !m.isAddOn && !!m.checkedInAt && !!m.checkedOutAt;

/**
 * Is this scan a REPEAT the console answers itself ("Already checked in / out / handed out")? Inside → a scan in is a
 * repeat; out → a scan out is. ⚠ Somebody who checked out is NOT "already in" — a scan in re-admits them (#228: before,
 * the console stopped them here with "Already checked in" and the server was never asked).
 */
export const isRepeatScan = (m: DoorMember, mode: DoorMode): boolean =>
  mode === "check_in" ? !!m.checkedInAt && !isReentryScan(m, mode) : mode === "check_out" ? !!m.checkedOutAt : false;

/**
 * #228 — a scan OUT of somebody who never came in: nothing to check out (the server records no exit without an arrival),
 * so the console says so instead of "Checked out". ⚠ Never a VOLUNTEER: one let in for their shift (a date they did not
 * book, a ticket they hold) has no stamp of their own, and their check-out is what closes the shift — without it the
 * shift stays open and is never paid.
 */
export const isOutWithoutIn = (m: DoorMember, mode: DoorMode): boolean => mode === "check_out" && !m.isAddOn && !m.isVolunteer && !m.checkedInAt;

/** #228 — what a scan DID, as the server answers it (`scanOutcome`). */
export const SCAN_OUTCOME = {
  FIRST_IN: "first_in",
  REENTRY: "reentry",
  ALREADY_IN: "already_in",
  OUT: "out",
  ALREADY_OUT: "already_out",
  NOTHING: "nothing",
} as const;

/**
 * Did the scan change anything? A repeat ("already in / out") or a check-out of somebody never in did not — a bulk action
 * counts only what it did. An answer with no outcome (an older API) is taken as done, as before.
 */
export const scanDidSomething = (outcome: string | null | undefined): boolean =>
  !outcome || !([SCAN_OUTCOME.ALREADY_IN, SCAN_OUTCOME.ALREADY_OUT, SCAN_OUTCOME.NOTHING] as string[]).includes(outcome);

/** #228 — one movement of a scan's door trail (`admissionTrail`). */
export interface DoorTrailMove {
  direction?: string | null;
  isReentry?: boolean | null;
  at?: string | null;
}

/**
 * #228 — the card's trail once somebody has come BACK in: "In 7:02 PM · Out 9:10 PM · Back in 9:30 PM". Null while there is
 * no re-entry — the In / Out badges already say everything then.
 */
export const doorTrailLine = (
  trail: DoorTrailMove[] | null | undefined,
  time: (at: string) => string,
  words: { in: string; out: string; backIn: string }
): string | null => {
  const moves = (trail ?? []).filter((m) => !!m?.at);

  if (!moves.some((m) => m.isReentry)) {
    return null;
  }

  return moves.map((m) => `${m.direction === "out" ? words.out : m.isReentry ? words.backIn : words.in} ${time(m.at as string)}`).join(" · ");
};

/**
 * Who the party's "… all" button works on, at a point doing `activity`: at a hand-out point the order's add-ons not
 * handed out yet (handing out is the add-on's check-in; nothing is checked out there); anywhere else its people still to
 * check in (or out) — never its add-ons, which are handed out one by one, where they are given. `actionable` is the
 * console's own rule for a member in a mode (a dead ticket, a refused holder).
 */
export const partyTargets = <T extends DoorMember>(
  members: T[],
  mode: DoorMode,
  activity: ServicePointActivity,
  actionable: (m: T, mode: DoorMode) => boolean
): T[] => {
  if (mode === "verify") {
    return [];
  }

  if (activity === "hand_out") {
    return mode === "check_in" ? members.filter((m) => m.isAddOn && actionable(m, "check_in") && !m.checkedInAt) : [];
  }

  // #228 — the people still to check in include those who went out (a re-entry); those still inside are not asked again.
  return members.filter((m) => !m.isAddOn && actionable(m, mode) && !isRepeatScan(m, mode) && !isOutWithoutIn(m, mode));
};

/** Does a point check people in (and out)? — check-in, or both (mirrors `servicePointChecksIn`). ⚠ Not "anything but
 *  hand-out": a lost & found desk is neither. */
export const pointChecksIn = (p: { kind?: string | null; activity?: string | null } | null | undefined): boolean =>
  ["check_in", "both"].includes(pointActivity(p));

/** Does a point hand out ticket add-ons? — hand-out, or both (mirrors `servicePointHandsOut`). */
export const pointHandsOut = (p: { kind?: string | null; activity?: string | null } | null | undefined): boolean =>
  ["hand_out", "both"].includes(pointActivity(p));

/** A lost & found desk (#68) — offered first where a desk is picked, never at the door. */
export const pointIsLostFound = (p: { kind?: string | null; activity?: string | null } | null | undefined): boolean =>
  pointActivity(p) === SERVICE_POINT_ACTIVITY.LOST_FOUND;

/** What a scanning page is for — it offers the kinds that fit it FIRST (every point stays choosable). */
export type ServicePointPurpose = "door" | "redeem" | "volunteer";

const PREFERRED: Record<ServicePointPurpose, ServicePointKind[]> = {
  // The check-in console: entrances and desks — and the merch table, where add-ons are handed out on the same console.
  door: ["entrance", "reception", "merch"],
  redeem: ["bar_food", "merch"],
  volunteer: ["reception", "entrance"],
};

export interface PointLike {
  id: string;
  name?: string | null;
  kind?: string | null;
  activity?: string | null;
  venueName?: string | null;
}

/**
 * The points in the order this page offers them: its kinds first (in that order), then the rest, each in their own order.
 * ⚠ The door never offers a lost & found desk (#68): it neither checks in nor hands out, so standing "there" would make
 *   the console do nothing it is for.
 */
export const pointsForPurpose = <T extends PointLike>(points: T[], purpose: ServicePointPurpose): T[] => {
  const rank = (p: T): number => {
    const i = PREFERRED[purpose].indexOf((p.kind ?? "other") as ServicePointKind);

    return i < 0 ? PREFERRED[purpose].length : i;
  };

  return points
    .filter((p) => purpose !== "door" || !pointIsLostFound(p))
    .map((p, i) => ({ p, i }))
    .sort((a, b) => rank(a.p) - rank(b.p) || a.i - b.i)
    .map(({ p }) => p);
};

/** "North gate · Riverside Hall" — the venue named only when the event spans more than one place. */
export const servicePointLabel = (p: PointLike | null | undefined, withVenue = false): string => {
  if (!p) {
    return "";
  }

  const name = (p.name ?? "").trim() || "A point";

  return withVenue && p.venueName ? `${name} · ${p.venueName}` : name;
};

/** Where a device remembers the point it stands at — per event and per page purpose (a door and a bar differ). */
export const servicePointStorageKey = (eventId: string, purpose: ServicePointPurpose): string => `em:servicePoint:${eventId}:${purpose}`;

/** A remembered point only while the event still offers it (removed, or never this event's → nothing). */
export const rememberedPoint = (remembered: string | null | undefined, points: PointLike[]): string | null =>
  remembered && points.some((p) => p.id === remembered) ? remembered : null;
